import type { User } from 'interfaces';
import isEmpty from 'lodash/isEmpty';

import { LIFETIME_SUBSCRIPTION_PLAN_NAMES } from 'utils/constants';
import type { Nullable } from 'utils/types';

export const canUpgrade = (user: Nullable<User>) => {
  return !hasSubscription(user) || !isPremium(user);
};

export const hasSubscription = (user: Nullable<User>): user is User & { subscription: Exclude<User['subscription'], undefined> } => {
  return !isEmpty(user?.subscription);
};

export const getSubscriptionStatus = (user: Nullable<User>) => {
  if (!hasSubscription(user)) return null;
  return user.subscription.status;
};

export const getSubscriptionRenewalStatus = (user: Nullable<User>) => {
  if (!hasSubscription(user)) return null;
  return user.subscription.renewalStatus;
};

export const getSubscriptionSource = (user: Nullable<User>) => {
  if (!hasSubscription(user)) return null;
  return user.subscription.plan.source;
};

export const getSubscriptionRenewalFrequency = (user: Nullable<User>) => {
  if (!hasSubscription(user)) return null;
  return user.subscription.plan.renewalFrequency;
};

export const getSubscriptionCurrency = (user: Nullable<User>, defaultCurrency = 'usd') => {
  if (!hasSubscription(user)) return defaultCurrency;
  return user.subscription.localCurrency ?? defaultCurrency;
};

export const isApple = (user: Nullable<User>) => {
  return getSubscriptionSource(user) === 'apple';
};

export const isCanceled = (user: Nullable<User>) => {
  return getSubscriptionStatus(user) === 'canceled';
};

export const isExpired = (user: Nullable<User>) => {
  return getSubscriptionStatus(user) === 'expired';
};

export const isOnTrial = (user: Nullable<User>) => {
  // We see some weird paypal subscription plan that is on trial for 365 days though it requires full payment upfront
  // We have number of users affected by this, so we need to check for this case
  return (
    hasSubscription(user) && user.subscription.plan.hasTrial && user.subscription.isOnTrial === true && user.subscription.plan.initialTrialDurationDays !== 365
  );
};

export const isLifetime = (currentUser: Nullable<User>) =>
  currentUser?.subscription?.plan?.name && LIFETIME_SUBSCRIPTION_PLAN_NAMES.includes(currentUser.subscription.plan.name);

export const isPayPal = (user: Nullable<User>) => {
  return getSubscriptionSource(user) === 'paypal';
};

export const isPlayStore = (user: Nullable<User>) => {
  return getSubscriptionSource(user) === 'play_store';
};

export const isPremium = (user: Nullable<User>) => {
  return hasSubscription(user) && user.entitlements?.isPremium === true;
};

export const isStripe = (user: Nullable<User>) => {
  return getSubscriptionSource(user) === 'stripe';
};

export const getSubscriptionDiscountType = (user: Nullable<User>) => {
  if (!hasSubscription(user)) return undefined;
  const discount = user.subscription.discounts?.[0];
  if (!discount) return undefined;

  return discount.type;
};

export const isAmountDiscountSubscription = (user: Nullable<User>) => {
  const discountType = getSubscriptionDiscountType(user);
  return discountType === 'amount';
};

export const isPercentageDiscountSubscription = (user: Nullable<User>) => {
  const discountType = getSubscriptionDiscountType(user);
  return discountType === 'percentage';
};

export const hasDiscount = (user: Nullable<User>) => {
  return hasSubscription(user) && !!user.subscription.discounts?.[0]?.value;
};

export const getSubscriptionDiscountPercentageValue = (user: Nullable<User>) => {
  return hasSubscription(user) && isPercentageDiscountSubscription(user) ? user.subscription.discounts?.[0]?.value : undefined;
};

export const getSubscriptionDiscountAmountValue = (user: Nullable<User>) => {
  return hasSubscription(user) && isAmountDiscountSubscription(user) ? user.subscription.discounts?.[0]?.value : undefined;
};

export const isMonthlySubscription = (user: Nullable<User>) => getSubscriptionRenewalFrequency(user) === 'monthly';

export const isYearlySubscription = (user: Nullable<User>) => {
  const renewalFrequency = getSubscriptionRenewalFrequency(user);
  return renewalFrequency === 'annually';
};
